import { CellView } from './CellView';
import { ColsView } from './ColsView';
import { CornerView } from './CornerView';
import { RichTextEditor, finishEditing } from './RichTextEditor';
import { RowsView } from './RowsView';
import { SelectedCell } from './SelectedCell';
import { COLS_BAR_HEIGHT, ROWS_BAR_WIDTH, VISIBLE_MARGIN } from './consts';
import type { Sheet } from '@/model/sheet';
import { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import { type ReactNode, useEffect, useRef, useState } from 'react';

export const $sheet = atom<Sheet | null>(null);

export const $rerender = atom({});

export function rerenderSheet() {
  $rerender.set({});
}

export function SheetView({ sheet }: { sheet: Sheet }) {
  useStore($rerender);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const container = containerRef.current!;
    if (!container) return;

    function onResize() {
      setWidth(container.offsetWidth);
      setHeight(container.offsetHeight);
    }

    function onScroll() {
      setScrollX(container.scrollLeft);
      setScrollY(container.scrollTop);
    }

    onResize();

    window.addEventListener('resize', onResize);
    container.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('resize', onResize);
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (sheet) {
      $sheet.set(sheet);
    }

    return () => {
      $sheet.set(null);
    };
  }, [sheet]);

  const visibleArea = sheet.getVisibleRange(
    scrollX - VISIBLE_MARGIN,
    scrollX + width + VISIBLE_MARGIN,
    scrollY - VISIBLE_MARGIN,
    scrollY + height + VISIBLE_MARGIN,
  );

  const visibleCells: ReactNode[] = [];

  let posX = visibleArea.startCol.accum;
  let posY = visibleArea.startRow.accum;

  for (let i = visibleArea.startCol.index; i <= visibleArea.endCol.index; i++) {
    const colWidth = sheet.getColWidth(i);
    for (
      let j = visibleArea.startRow.index;
      j <= visibleArea.endRow.index;
      j++
    ) {
      const rowHeight = sheet.getRowHeight(j);

      const pos = new Vector2(posX, posY);
      const size = new Vector2(colWidth, rowHeight);
      const cellPos = new Vector2(i, j);
      visibleCells.push(
        <CellView
          key={pos.s}
          pos={pos}
          size={size}
          cellPos={cellPos}
          sheet={sheet}
        />,
      );

      posY += rowHeight;
    }

    posX += colWidth;
    posY = visibleArea.startRow.accum;
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full grow overflow-auto bg-white"
      onMouseDown={() => finishEditing()}
    >
      <div
        className="absolute"
        style={{
          translate: `${ROWS_BAR_WIDTH}px ${COLS_BAR_HEIGHT}px`,
        }}
      >
        {visibleCells}
        <SelectedCell sheet={sheet} />
        <RichTextEditor />
      </div>
      <div
        className="sticky top-0"
        style={{
          translate: `${ROWS_BAR_WIDTH}px 0`,
        }}
      >
        <ColsView
          posX={visibleArea.startCol.accum}
          startCol={visibleArea.startCol.index}
          endCol={visibleArea.endCol.index}
          onSetColWidth={(idx, width) => {
            sheet.setColWidth(idx, width);
            rerenderSheet();
          }}
          sheet={sheet}
        />
      </div>
      <div
        className="sticky left-0"
        style={{
          translate: `0 ${COLS_BAR_HEIGHT}px`,
        }}
      >
        <RowsView
          posY={visibleArea.startRow.accum}
          startRow={visibleArea.startRow.index}
          endRow={visibleArea.endRow.index}
          onSetRowHeight={(idx, height) => {
            sheet.setRowHeight(idx, height);
            rerenderSheet();
          }}
          sheet={sheet}
        />
      </div>
      <CornerView />
      <div
        className="absolute"
        style={{
          top: scrollY + height,
          left: scrollX + width,
          width: 100,
          height: 100,
        }}
      />
    </div>
  );
}
